import { t } from "@lingui/core/macro";
import { Box, Dialog, AppBar, Tabs, Tab, DialogContent, Container, Button } from "@mui/material";
import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { Loader } from "../../loader";
import LabelTable from "../../labelTable";
import LabeledTextField from "../../labeledTextField/labeledTextField";
import UsageComponent from "../../customObjects/usageComponent";
import DataTable from "../../dataTable";
import GenericGridTable from "../../customObjects/genericGridTable";
import { capitalizeKeys } from "../../../helpers/capitalizeKeys";
import { parserDataTypes } from "../../customObjects/customObjectsPreData";

interface AddDialogProps {
    open: boolean;
    onClose: (updateStatus: "none" | "success" | "error" | "info" | "warning", reason?: string) => void;
    parentId: string;
    id: string | undefined;
    selectTemplate: "entryPoint" | "taxonomyElements" | "taxonomyDimensionValue";
}

const TaxonomyDialog = ({ open, onClose, parentId, id, selectTemplate }: AddDialogProps) => {
    const [data, setData] = useState<any | null>(null);
    const [languages, setLanguages] = useState<string[]>([]);
    const [labelTableData, setLabelTableData] = useState<any[]>([]);
    const templateConfig = {
        entryPoint: {
            tabs: ["details"],
            details: [
                { name: t`Schema Ref`, key: "schemaRef", fieldType: "text", value: data?.schemaRef },
                { name: t`Target namespace`, key: "targetNamespace", fieldType: "text", value: data?.targetNamespace },
            ],
        },
        taxonomyElements: {
            tabs: ["details", "labels", "usage"],
            details: [
                { key: "name", name: t`Name`, fieldType: "text", disabled: true, value: data?.name },
                { key: "nameSpace", name: t`Name Space`, fieldType: "text", disabled: true, value: data?.ns?.href },
                { key: "periodType", name: t`Period Type`, fieldType: "text", disabled: true, value: data?.periodType },
                { key: "taxonomyDataType", name: t`Taxonomy Data Type`, fieldType: "text", disabled: true, value: data?.taxonomyDataType },
                { key: "parserDataType", name: t`Parser Data Type`, fieldType: "parserDataType", value: data?.parserDataTypeId, disabled: true, endpointUrl: parserDataTypes },
                { key: "isAbstract", name: t`Is Abstract`, fieldType: "text", disabled: true, value: data?.isAbstract?.toString() },
                { key: "isCredit", name: t`Is Credit`, fieldType: "checkbox", value: data?.isCredit, disabled: true },
                { key: "invertSign", name: t`Invert Sign`, fieldType: "checkbox", value: data?.invertSign },
                { key: "rawNumber", name: t`Raw Number`, fieldType: "checkbox", value: data?.rawNumber },
            ],
        },
        taxonomyDimensionValue: {
            tabs: ["Dimension Value", "Child Dimension Values"],
            "Dimension Value": [
                { key: "dimension", name: t`Dimension`, fieldType: "dimension", value: data?.dimension, isAlternate: "alternateDimension", includeCustom: false, showDetailed: false, disabled: !data?.isCustom },
                { key: "element", name: t`Element`, fieldType: "element", disabled: true, value: data?.element },
                { key: "value", name: t`Value`, fieldType: "text", disabled: true, value: data?.value },
                { key: "isTypedMember", name: t`Is Typed Member`, fieldType: "alternateCheckbox", value: data?.isTypedMember ?? null, disabled: !data?.isCustom },
                { key: "isDefault", name: t`Is Default Member`, fieldType: "alternateCheckbox", value: data?.isDefault ?? null, disabled: !data?.isCustom },
                { key: "parentMember", name: t`Parent Member`, fieldType: "dimension", isAlternate: "alternateParentMemberDimension", value: data?.parentMember?.name ?? null, includeCustom: false, showDetailed: false, disabled: !data?.dimension || !data?.isCustom },
            ],
        }
    };

    const [activeTab, setActiveTab] = useState<string>(templateConfig[selectTemplate].tabs[0]);
    const [cacheBuster, setCacheBuster] = useState(crypto.randomUUID());
    const [isLoading, setIsLoading] = useState(true);
    const hasRun = useRef(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    selectTemplate === "entryPoint"
                        ? `api/taxonomies/${parentId}/entrypoints/${id}/configuration`
                        : selectTemplate === "taxonomyElements"
                            ? `api/taxonomies/${parentId}/elements/${id}`
                            : selectTemplate === "taxonomyDimensionValue"
                                ? `api/taxonomies/${parentId}/dimension-values/${id}`
                                : ``,
                    {
                        params: { cb: cacheBuster }
                    }
                );
                setData(response.data);

                if (selectTemplate === "entryPoint") {
                    const languagesResponse = await axios.get(`api/taxonomies/languages?taxonomyIds=${parentId}`, {
                        params: { cb: cacheBuster }
                    });
                    setLanguages(languagesResponse.data);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        if (open && !hasRun.current) {
            fetchData();
            hasRun.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, open, parentId, cacheBuster]);

    useEffect(() => {
        if (activeTab === "labels") {
            const getLabelData = async () => {
                try {
                    const response = await axios.get(
                        `api/taxonomies/${parentId}/elements/${id}/labels`,
                        {
                            params: { cb: cacheBuster }
                        }
                    );
                    setLabelTableData(response.data);
                    setIsLoading(false);
                } catch (error) {
                    console.error("Error fetching label data:", error);
                }
            }
            if (!labelTableData.length) {
                setIsLoading(true);
                getLabelData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleTabChange = (event: any, newValue: string) => {
        setActiveTab(newValue);
    };

    const handleApply = async () => {
        try {
            const capitalizeData = capitalizeKeys(data);
            if (selectTemplate === "entryPoint") {
                await axios.put(`api/taxonomies/${parentId}/entrypoints/${id}/configuration`, capitalizeData);
            } else if (selectTemplate === "taxonomyElements") {
                await axios.put(`api/taxonomies/${parentId}/elements/${id}`, capitalizeData);
            } else if (selectTemplate === "taxonomyDimensionValue") {
                await axios.put(`api/taxonomies/${parentId}/dimension-values/${id}`, capitalizeData);
            }
            onClose("success", t`Changes applied successfully`);
            setData(null);
            setLabelTableData([]);
            hasRun.current = false;
            setCacheBuster(crypto.randomUUID());
        } catch (error: any) {
            console.error("Error applying changes:", error);
            onClose("error", error.isTranslated ? error.exceptions : "");
        }
    };

    const handleClose = () => {
        onClose("none");
        setData(null);
        setLabelTableData([]);
        setActiveTab(templateConfig[selectTemplate].tabs[0]);
        hasRun.current = false;
    };

    return (
        <Dialog
            open={open}
            maxWidth={false}
            fullWidth
            onClose={handleClose}
            sx={{
                maxWidth: window.innerWidth > 1920 ? '50vw' : '90vw',
                margin: 'auto',
                maxHeight: "90vh",
                p: 0,
            }}
        >
            <AppBar position="static">
                <Tabs value={activeTab} onChange={handleTabChange}>
                    {templateConfig[selectTemplate].tabs.map(key => (
                        <Tab
                            sx={{ color: "white" }}
                            key={key}
                            value={key}
                            label={data?.[key]?.navBarName || key}
                        />
                    ))}
                </Tabs>
            </AppBar>
            <DialogContent sx={{
                maxHeight: "85vh",
                minHeight: "60vh",
                overflowY: "auto",
                overflowX: "hidden",
                width: "100%",
                minWidth: "100%",
                p: 0,
                pt: 2
            }}>
                {isLoading ? (
                    <Loader height={500} />
                ) : (
                    <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth={false}>
                        {(() => {
                            if (!data) {
                                return <Box>{t`No data available`}</Box>;
                            }
                            if (isLoading) {
                                return <Loader height={500} />;
                            }
                            if (activeTab === "details" || activeTab.replace(/\s+/g, '') === "DimensionValue") {
                                return (
                                    <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth={false}>
                                        {templateConfig[selectTemplate]?.[activeTab as keyof typeof templateConfig[typeof selectTemplate]]?.map((field: any) => (
                                            <Box key={field.key} sx={{ mb: 2 }}>
                                                <LabeledTextField
                                                    label={field.name}
                                                    value={field.value}
                                                    disabled={field.disabled}
                                                    fieldType={field.fieldType}
                                                    options={field?.options || []}
                                                    endpointUrl={field?.endpointUrl}
                                                    isAlternate={field?.isAlternate}
                                                    includeCustom={field?.includeCustom}
                                                    showDetailed={field?.showDetailed}
                                                    dimensionName={data?.dimension?.name || undefined}
                                                    taxonomyId={parentId}
                                                    onValueChange={(value) => {
                                                        setData((prevData: any) => ({
                                                            ...prevData,
                                                            [field.key]: value,
                                                        }));
                                                    }}
                                                />
                                            </Box>
                                        ))}

                                        {selectTemplate === "entryPoint" && activeTab === "details" && (
                                            <LabelTable
                                                languages={languages || []}
                                                data={data?.labels || []}
                                                setData={(newData: any) =>
                                                    setData({ ...data, labels: newData })
                                                }
                                            />
                                        )}
                                    </Container>
                                );
                            }

                            if (activeTab === "labels") {
                                return (
                                    <DataTable
                                        data={labelTableData}
                                        loading={isLoading}
                                        columns={[
                                            { header: { id: "label", label: t`Label`, }, key: "label" },
                                            { header: { id: "labelRole", label: t`Role`, }, key: "labelRole" },
                                            { header: { id: "language", label: t`Language` }, key: "language" },
                                        ]}
                                        noWidth
                                        noDataMessage={t`No Labels`}
                                    />
                                )
                            }

                            if (activeTab.replace(/\s+/g, '') === "usage") {
                                return (
                                    <UsageComponent
                                        id={parentId}
                                        activeTab={selectTemplate}
                                        alternateId={id}
                                    />
                                )
                            }

                            if (activeTab.replace(/\s+/g, '') === "ChildDimensionValues") {
                                return (
                                    <GenericGridTable
                                        disabled={!data?.isCustom}
                                        data={data?.childDimensionValues || []}
                                        setData={() => { }}
                                        type={"childElementReferences"}
                                        id={data?.id || ""}
                                        isEdiTable={false}
                                    />
                                )
                            }

                            if (activeTab === "labels" || activeTab === "customLabels") {
                                return (
                                    <LabelTable
                                        languages={languages || []}
                                        data={data?.[activeTab] || []}
                                        setData={(newData: any) => setData({ ...data, [activeTab]: newData })}
                                    />
                                );
                            }

                            return <Box>{t`No configuration available for this tab.`}</Box>;
                        })()}

                    </Container>
                )}
            </DialogContent>
            <Box display="flex" justifyContent="flex-end" m={2}>
                <Container
                    maxWidth={false}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                    }}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClose}
                    >
                        {t`Close`}
                    </Button>
                    <Button
                        disabled={selectTemplate === "taxonomyDimensionValue" && !data?.isCustom}
                        sx={{ ml: 2 }}
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                    >
                        {t`Apply`}
                    </Button>
                </Container>
            </Box>
        </Dialog >
    );
};

export default TaxonomyDialog;
